import { datadogRum } from '@datadog/browser-rum'
import useSetCheckoutRuleSession from '../../graphQL/mutations/setCheckoutRuleSession'
import { CheckoutRules, type CheckoutRuleDocumentType } from '../../interfaces'
import type { SessionCheckoutRuleValue } from '../../graphQL/queries/getSession/types'
import { addAlert, setSessionRulePick } from '../../redux/slices/config'
import {
  clearSelection,
  releaseFailedAutoSettle,
  setGuidedReturn,
  setSelection,
} from '../../redux/slices/checkout'
import type { ICheckoutSelectionValue } from '../../redux/slices/checkout/types'
import {
  isUsableOption,
  selectFirstHeldRule,
  selectRuleIsSettled,
} from '../../utils/checkoutRuleSelection'
import { generateDataDogContext } from '../../utils/dataDog'
import getAlertErrorMessage, {
  AlertMessageType,
} from '../../utils/getAlertErrorMessage'
import {
  calendarDayInZone,
  daysBetweenCalendarDays,
} from '../../utils/calendarToday'
import { fireGTMEvent } from '../../utils/fireGTMEvent'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import useAppStore from '../useAppStore'
import { useDynamicBack } from '../useBack'
import usePushView from '../usePushView'

const FUNCTION_NAME = 'useCheckoutRuleSelection'

/**
 * Selections whose own write was rejected, by object identity (COM-1765).
 *
 * A rollback restores the selection that was in the slice when its tap
 * happened. When two taps overlap, that `previous` can be the older tap's
 * value, and the older write may already have failed while the newer one was
 * in flight: its catch left the slice alone because the newer tap owned it.
 * Restoring it would put a value the server just refused back on screen, so
 * the rollback asks this set first.
 *
 * Identity, not `data`/`key`, because the slice stores the very object
 * `select` built (`setSelection` assigns it; RTK freezes in place rather than
 * copying), and because a re-tap of the same option is a new attempt that
 * must not inherit the old one's failure. A `WeakSet`, so nothing here
 * outlives the selection it describes and no store or test needs a reset.
 */
const rejectedSelections = new WeakSet<ICheckoutSelectionValue>()

/**
 * The server's echo of the stored pick, when it is one (COM-1765).
 *
 * `setCheckoutRuleSession { value }` is the raw JSON the server stored, which
 * carries the `amount` and `attributes` it copied from the offered option, so
 * it is a better snapshot entry than the `{ data, key? }` that was sent. The
 * field is `JSON` and nothing validates it, so anything that is not an object
 * with a non-empty string `data` (including a mock that resolves with no
 * data) falls back to the sent value.
 *
 * An echo without a usable `key` keeps the sent one. `selectRulePickId`
 * matches a keyed row on `key ?? data`, so a keyless entry would mark no row
 * on a keyed list (or the wrong one where two rows share `data`).
 */
const storedPick = (
  echo: unknown,
  sent: ICheckoutSelectionValue,
): SessionCheckoutRuleValue | undefined => {
  if (typeof echo !== 'object' || echo === null) {
    return undefined
  }
  const stored = echo as SessionCheckoutRuleValue
  if (typeof stored.data !== 'string' || stored.data === '') {
    return undefined
  }
  const echoedKey = typeof stored.key === 'string' && stored.key !== ''
  if (!echoedKey && sent.key) {
    return { ...stored, key: sent.key }
  }
  return stored
}

/** How a caller other than a picker screen wants `select` to behave. */
export interface CheckoutRuleSelectionOptions {
  /**
   * Suppress the failure toast. The rollback and the Datadog report stay.
   *
   * For the writes no shopper tapped: COM-1048's auto-settle, which writes a
   * one-option rule on cart entry, and COM-1501's `startGuidedWalk`, which
   * makes the same decision from the catalog. A toast reading "we could not
   * save your payment method" over a tap they never made tells them something
   * is wrong and gives them nothing to do about it — and the recovery is
   * automatic, through COM-1046's *Continuar* flow onto the picker.
   *
   * Silent or not, an accepted write releases a `'failed'` rule
   * (`releaseIfFailed` below). So a silent write that lands after the
   * deadline already settled its rule `'failed'` releases it too, and the
   * summary CTA can flip to place-order without a tap.
   *
   * Belt-and-braces rather than mechanism: `select` already raises nothing
   * when the copy is unauthored, because `getAlertErrorMessage` returns `''`
   * and the dispatch is guarded on it. This says so at the call site instead
   * of leaving it to a storefront's text file.
   *
   * ⚠️ It suppresses **only** the toast. The Datadog error still fires, and it
   * has to: a write the shopper never asked for is the one failure they cannot
   * report themselves.
   */
  silent?: boolean
}

/** `select` as a caller holds it: write one value, answer whether it landed. */
export type CheckoutRuleSelect = (
  chosen: string,
  options?: CheckoutRuleSelectionOptions,
) => Promise<boolean>

/**
 * Choosing, confirming and leaving — for either picker screen.
 *
 * The two screens' write paths differ in three values and nothing else: the
 * rule type, the toast to raise when the write fails, and the name of the
 * field their action's args arrive under. The first two are parameters here;
 * the third stays in each screen's own hook, because that is the part a
 * storefront authors and it reads better named — `setDeliveryDate({ date })`
 * rather than a generic `select`.
 *
 * Extracted rather than copied because the interesting part is the rollback,
 * and a second copy of it is a second place for a rollback bug to live. Same
 * reasoning as `useCheckoutRuleOptions` on the read side.
 */
export const useCheckoutRuleSelection = (
  ruleType: CheckoutRuleDocumentType,
  alertType: AlertMessageType,
  { silent = false }: CheckoutRuleSelectionOptions = {},
) => {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const pushView = usePushView()
  const goBack = useDynamicBack()
  const slug = useAppSelector((state) => state.defaultSlice.storeName)
  const sessionId = useAppSelector((state) => state.defaultSlice.sessionId)
  const texts = useAppSelector((state) => state.defaultSlice.config?.texts)
  const [writeSelection] = useSetCheckoutRuleSession()

  /**
   * Releases a `'failed'` rule once this pick has landed (COM-1501).
   *
   * `'failed'` is held by `selectFirstHeldRule` whatever the shopper picks,
   * and nothing else moves it, so without this a failed auto-write held the
   * summary at *Continuar* for the rest of the session. Called only after the
   * server answered, so neither the optimistic `setSelection` nor a rejected
   * write can release anything.
   *
   * **Only for the pick still in the slice.** Two taps put two writes in
   * flight; the first to answer may be the older one, and releasing on its
   * say-so would vouch for a value the shopper has already replaced. A write
   * that lands after the deadline settled the rule `'failed'` releases it
   * too, which is right: the value it wrote is on the session.
   *
   * Read fresh, after the await, for `select`'s own reason. The reducer
   * refuses any status but `'failed'`; the check here is what keeps every
   * other successful pick from dispatching at all.
   */
  const releaseIfFailed = (value: ICheckoutSelectionValue) => {
    const checkout = store.getState().checkoutSlice
    if (checkout.autoSettle?.[ruleType] !== 'failed') {
      return
    }

    const latest = checkout.selections[ruleType]
    if (latest?.data !== value.data || latest?.key !== value.key) {
      return
    }

    const usable = (checkout.available[ruleType]?.data ?? []).filter(
      isUsableOption,
    )
    dispatch(
      releaseFailedAutoSettle({
        ruleType,
        status: usable.length > 1 ? 'multiple' : 'settled',
      }),
    )
  }

  /**
   * Undoes a rejected write's optimistic pick, if the slice still shows it.
   *
   * **Only while the slice still holds this call's own `value`** (by
   * identity, see `rejectedSelections`). A newer tap that is still in flight
   * owns the slice, and restoring this call's `previous` over it would leave
   * the screen showing one pick while the newer write puts another on the
   * session.
   *
   * **What it restores is `previous` unless that one was rejected too.** With
   * two overlapping taps, `previous` is the older tap's value. If that write
   * is confirmed or still in flight, it is the shopper's live choice and goes
   * back on screen (and a later failure of its own rolls back in turn). If it
   * already failed, the honest answer is no fresh pick at all: the slice is
   * cleared and every reader falls back to the session snapshot, which holds
   * the last pick the server confirmed. Cost: `confirmDisabled` reads only
   * the slice, so the picker's confirm CTA disables until the shopper taps
   * again.
   *
   * `setSelection` cannot express "there was no pick", so a failure on the
   * shopper's *first* choice needs `clearSelection` too, otherwise the row
   * stays highlighted over a session that carries nothing and the confirm
   * CTA stays enabled.
   */
  const rollBack = (
    value: ICheckoutSelectionValue,
    previous: ICheckoutSelectionValue | undefined,
  ) => {
    if (store.getState().checkoutSlice.selections[ruleType] !== value) {
      return
    }

    if (previous && !rejectedSelections.has(previous)) {
      dispatch(setSelection({ ruleType, value: previous }))
      return
    }

    dispatch(clearSelection({ ruleType }))
  }

  /**
   * Records the pick optimistically, then persists it.
   *
   * **The store is read through `useAppStore`, not `useAppSelector`.** The
   * options and the previous selection are read when the shopper taps, and the
   * rollback runs a round trip after that — a render-time snapshot would be as
   * old as the closure and would restore a selection that had already moved.
   * The same reason the boot prefetch hooks read fresh (COM-974).
   *
   * `chosen` is already extracted and coerced by the caller. An empty string
   * means the args carried no usable value, and leaves the shopper where they
   * are.
   *
   * `options` overrides the hook's own for this one call. COM-1501's
   * `startGuidedWalk` writes through the picker screens' instances with
   * `{ silent: true }` rather than mounting two more of this hook in every
   * `EventManager`.
   *
   * **Answers whether the write landed**, `false` on both the early return and
   * the rejection. The picker screens ignore it — their answer is the toast
   * and the rolled-back row. COM-1048's auto-settle cannot: it latches its
   * status before the `await`, so it needs to be told which terminal value to
   * settle on. Same shape, and the same reason, as COM-974's prefetch hooks.
   */
  const select = async (
    chosen: string,
    options: CheckoutRuleSelectionOptions = {},
  ): Promise<boolean> => {
    if (!chosen || !slug || !sessionId) return false

    const checkout = store.getState().checkoutSlice
    const previous = checkout.selections[ruleType]
    /*
     * Match the offered option by `key` when the list is keyed, otherwise by
     * `data`. The caller passes a string that is one of the two — the
     * payment picker's injector forwards `key ?? data` as the option's id, so
     * `onMethodSelect(id)` arrives here already in the right shape, and the
     * delivery side has no key so this always matches `data`. Yao's §4: "A
     * list is either all keyed or not keyed at all." The match therefore
     * cannot ambiguate between the two keys.
     *
     * Falling back rather than refusing on a miss. The value always comes
     * from what the screen rendered, so a miss means the option list moved
     * under it — and refusing would give the shopper a tap that does nothing.
     *
     * ⚠️ On the delivery side the fallback is also what keeps R-8 intact: the
     * token is carried through as the caller gave it, never rebuilt.
     */
    const availableData = checkout.available[ruleType]?.data ?? []
    const offered = availableData.find(
      (option) => (option?.key ?? option?.data) === chosen,
    )
    /*
     * On a **keyed** list, a miss is not safe to fall back on: `chosen` is a
     * `key`, and writing `{ data: chosen }` would send the key where the
     * backend expects the data (`CREDIT_30D` instead of `CREDIT`), which
     * COM-1078 rejects as `selection_malformed`. Refuse the write and let the
     * screen re-render against the moved list. The delivery side is never
     * keyed (Yao's §4), so this never fires there, so R-8's token-through
     * guarantee holds unchanged.
     */
    const listIsKeyed = availableData.some((option) => !!option?.key)
    if (!offered && listIsKeyed) {
      return false
    }
    /*
     * The write carries `{ data, key? }` and nothing else. The backend
     * ([COM-1078](https://linear.app/yalo/issue/COM-1078)) copies the full
     * option — `amount`, `attributes`, every leaf of `attributes` — onto the
     * pick from its own list, so `amount` and `attributes` on the wire would
     * be ignored. Dropping them from the write closes the gap by construction
     * rather than relying on the server to.
     *
     * The slice stores the same shape through `setSelection`. Nothing in
     * cinnamon reads `amount` or `attributes` off a selection today, so
     * nothing stales.
     */
    const value: ICheckoutSelectionValue = offered
      ? {
          data: offered.data,
          ...(offered.key ? { key: offered.key } : {}),
        }
      : { data: chosen }

    dispatch(setSelection({ ruleType, value }))

    try {
      const result = await writeSelection({
        variables: {
          storefrontName: slug,
          sessionUid: sessionId,
          ruleType,
          value,
        },
      })

      /*
       * The server now holds `value`, so the session snapshot does too
       * (COM-1765). Without this the snapshot keeps the page-load pick, and a
       * later `clearSelection` (recovery, or this catch on a later first
       * write) hands every fallback reader that stale pick as if it were
       * current. After the await, never optimistically: a rejected write
       * changed nothing on the server.
       *
       * The entry is the server's echo when it is a pick (`storedPick`), so
       * the `attributes.label` the session-label fallback reads survives;
       * otherwise the sent `{ data, key? }`, whose `key` keeps
       * `selectRulePickId` matching a keyed row.
       *
       * ⚠️ Skipped when the store's session moved during the round trip: the
       * write landed on the session it was sent for, and patching it into a
       * different session's snapshot would show that session a pick it never
       * made. Defensive today: `sessionId` is written once from the URL, so
       * no production path moves it mid-flight. `releaseIfFailed` and the
       * answer stay as they are, because the write did land and the slice
       * they read is not session-scoped.
       *
       * Not gated on the slice still holding `value`, unlike
       * `releaseIfFailed`: with two writes in flight the
       * snapshot follows whichever answers last, which is not necessarily the
       * newer one. If both succeed and the older answers last, the snapshot
       * holds the older value while the slice holds the newer; the slice wins
       * while it holds a pick, so this shows only after a later
       * `clearSelection`. A write that timed out client-side after the server
       * committed it is unknowable here and leaves the snapshot on the
       * previous value.
       */
      if (store.getState().defaultSlice.sessionId === sessionId) {
        dispatch(
          setSessionRulePick({
            ruleType,
            value:
              storedPick(result?.data?.setCheckoutRuleSession?.value, value) ??
              value,
          }),
        )
      }
      releaseIfFailed(value)

      return true
    } catch (exception) {
      rejectedSelections.add(value)
      rollBack(value, previous)

      const message = getAlertErrorMessage(alertType, texts)
      const quiet = options.silent ?? silent
      if (!quiet && message) {
        dispatch(addAlert({ message }))
      }

      const context = generateDataDogContext({
        title: 'setCheckoutRuleSession failed for a checkout rule',
        extraInfo: { function: FUNCTION_NAME, ruleType },
      })
      datadogRum.startView(context.viewName)
      datadogRum.addError(
        exception instanceof Error
          ? exception
          : new Error('setCheckoutRuleSession failed'),
        context,
      )

      fireGTMEvent('commerce-checkout_selection_error', {
        rule_type:
          ruleType === CheckoutRules.DELIVERY_DATES
            ? 'delivery_date'
            : 'payment_method',
      })

      return false
    }
  }

  /**
   * Confirm. Back to the cart — unless the shopper is mid-walk.
   *
   * Four callers reach this one function. COM-1045's *Cambiar* row and
   * COM-1050's recovery redirect both start on the cart and end there.
   * COM-1046's guided walk starts on the summary and COM-1501's on the catalog
   * (`startGuidedWalk`), and each ends where it started, visiting every rule
   * the shopper still owes on the way: the last hop returns to
   * `checkoutSlice.guidedReturnPath`, wherever it was set. That field is how
   * the callers are told apart, and its absence is what keeps the cart push
   * the default rather than a branch.
   *
   * **The next step is derived, not queued.** `usePlaceOrderFlow` clears its
   * queue on unmount and `EventManager` mounts one per container, so no queue
   * survives a route change. `selectFirstHeldRule` answers again at every hop.
   *
   * `skip` is this screen's own rule type. A shopper who reaches confirm with
   * nothing chosen would otherwise be pushed at the screen they are standing
   * on — a button that visibly does nothing — because the rule they are
   * looking at is still the first one held.
   *
   * **Deliberately not gated on a selection existing**, unchanged and for the
   * unchanged reason: the CTA's `disabled` binds
   * `checkout.ui.<RULE>.confirmDisabled`, so a storefront that authored it
   * cannot reach here with nothing chosen, and one that did not should still
   * let the shopper leave.
   *
   * The store is read fresh rather than from a render closure. `EventManager`
   * registers its handlers in an effect keyed on the redux state, so a value
   * captured at render can be a navigation behind by the time the shopper
   * taps — the same reason `select` above reads fresh, and COM-974's rule.
   * It also matters within one tap: `select` writes its pick optimistically
   * before awaiting, so by now the rule just chosen already reads settled and
   * the walk moves past it.
   *
   * ⚠️ **Later hops replace rather than push.** Only the first hop, from the
   * screen the walk started on, is a push — so backing out of a picker lands
   * there: on the summary with the CTA still reading *Continuar*, or on the
   * catalog, which does not walk the shopper again (`guidedWalkStarts`).
   * Replacing the rest keeps history two deep, so Back from anywhere in the
   * walk reaches that screen instead of walking the shopper backwards through
   * pickers they just completed. It
   * also contains an origin-escaping path for free, which is why only the
   * first hop needs `ESCAPES_ORIGIN` — and `selectFirstHeldRule` applies that
   * to every path it returns anyway.
   */
  const confirm = () => {
    const state = store.getState()
    const storefront = state.defaultSlice.storeName
    const session = state.defaultSlice.sessionId
    if (!storefront || !session) return

    fireConfirmEvent(state)

    const guidedReturn = state.checkoutSlice?.guidedReturnPath
    if (!guidedReturn) {
      pushView({ path: `/${storefront}/${session}/cart` })
      return
    }

    const next = selectFirstHeldRule(state, ruleType)
    if (next) {
      // COM-1528: the guided-walk entry point has to travel with every hop,
      // not just the first push from the summary. Without the inner `state`
      // here, the next picker reads `useLocation().state?.entryPoint` as
      // undefined and defaults to `cart_change` — mis-attributing every
      // second picker in the walk.
      pushView({
        path: next.path,
        state: { replace: true, state: { entryPoint: 'guided_walk' } },
      })
      return
    }

    dispatch(setGuidedReturn(undefined))
    pushView({ path: guidedReturn, state: { replace: true } })
  }

  /**
   * GA4 analytics event for a confirmed picker choice. `add_shipping_info` for
   * delivery, `add_payment_info` for payment. No PII — `delivery_lead_days` is
   * days-from-today, never the raw date; `payment_type` is the option key.
   */
  const fireConfirmEvent = (state: ReturnType<typeof store.getState>) => {
    const selection = state.checkoutSlice?.selections?.[ruleType]
    const available = state.checkoutSlice?.available?.[ruleType]
    if (!selection) return

    const selectionId = selection.key ?? selection.data
    const preselected = available?.preselected ?? null
    const selectionChanged = preselected !== null && preselected !== selectionId
    const optionPosition = (available?.data ?? []).findIndex(
      (option) => option?.data === selection.data,
    )

    if (ruleType === CheckoutRules.DELIVERY_DATES) {
      const timezone =
        state.defaultSlice.sessionData?.configuration?.i18n?.timezone
      const today = calendarDayInZone(timezone)
      const leadDays = daysBetweenCalendarDays(today, selection.data) ?? 0

      fireGTMEvent('commerce-add_shipping_info', {
        delivery_lead_days: leadDays,
        option_position: optionPosition,
        selection_changed: selectionChanged,
      })
      return
    }

    fireGTMEvent('commerce-add_payment_info', {
      payment_type: selectionId,
      payment_method: selection.data,
      selection_changed: selectionChanged,
    })
  }

  /**
   * Leaving without choosing.
   *
   * `useDynamicBack` rather than a bare `navigate(-1)`: this is a webview a
   * shopper can be deep-linked into, and popping an empty history strands
   * them. It falls back to the storefront home when there is nothing to pop.
   *
   * Writes nothing. Opening a picker and backing out settles nothing, which is
   * what COM-1046 reads to decide whether its CTA still says *Continuar*.
   */
  const back = () => {
    // COM-1528: `had_selection` has to count persisted picks too — auto-settle
    // deliberately accepts a still-offered `sessionData.checkoutRules` pick
    // without copying it into `checkoutSlice.selections`, so a returning
    // shopper backing out of a highlighted picker would report `false` for
    // a selection the UI is already drawing.
    const hadSelection = selectRuleIsSettled(ruleType)(store.getState())
    fireGTMEvent('commerce-checkout_picker_back', {
      rule_type:
        ruleType === CheckoutRules.DELIVERY_DATES
          ? 'delivery_date'
          : 'payment_method',
      had_selection: hadSelection,
    })
    goBack({})
  }

  return { select, confirm, back }
}

export default useCheckoutRuleSelection
